/**
 * Tier List Component
 * Feature: 024-sla-tier-configuration
 *
 * Table display for SLA tiers with actions.
 */

import React, { useState } from 'react';
import { SLATier } from '@shared/types/sla';
import * as slaService from '../../services/slaService';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';
import { Modal, Button } from '../ui';

interface TierListProps {
  tiers: SLATier[];
  onEdit: (tier: SLATier) => void;
  onRefresh: () => void;
}

export const TierList: React.FC<TierListProps> = ({ tiers, onEdit, onRefresh }) => {
  const [deletingTier, setDeletingTier] = useState<SLATier | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [affectedClients, setAffectedClients] = useState<any[]>([]);
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(tiers);

  const handleDelete = async (tier: SLATier) => {
    try {
      // Check affected clients
      const clients = await slaService.getAffectedClients(tier.id);
      setAffectedClients(clients);
      setDeletingTier(tier);
      setDeleteConfirm(true);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to check tier');
    }
  };

  const confirmDelete = async (force: boolean) => {
    if (!deletingTier) return;

    try {
      await slaService.deleteTier(deletingTier.id, force);
      setDeleteConfirm(false);
      setDeletingTier(null);
      setAffectedClients([]);
      onRefresh();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete tier');
    }
  };

  return (
    <div className='bg-[var(--color-bg-card)] rounded-lg shadow overflow-hidden'>
      <DualScrollTable>
      <table className='min-w-full divide-y divide-[var(--color-border)]'>
        <thead className='bg-[var(--color-bg-subtle)]'>
          <tr>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Tier Name
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Uptime %
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              RTO (hours)
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              RPO (hours)
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Penalty ($/hr)
            </th>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Status
            </th>
            <th className='px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
          {pageItems.map((tier) => (
            <tr key={tier.id} className={tier.is_deprecated ? 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]' : ''}>
              <td className='px-6 py-4 whitespace-nowrap'>
                <div className='flex items-center'>
                  <span className='font-medium'>{tier.tier_name}</span>
                  {tier.is_deprecated && (
                    <span className='ml-2 px-2 py-1 text-xs bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] rounded'>
                      Deprecated
                    </span>
                  )}
                </div>
              </td>
              <td className='px-6 py-4 whitespace-nowrap'>{tier.uptime_percent.toFixed(2)}%</td>
              <td className='px-6 py-4 whitespace-nowrap'>{tier.rto_hours}</td>
              <td className='px-6 py-4 whitespace-nowrap'>{tier.rpo_hours}</td>
              <td className='px-6 py-4 whitespace-nowrap'>
                ${tier.penalty_per_hour_usd.toFixed(2)}
              </td>
              <td className='px-6 py-4 whitespace-nowrap'>
                {tier.is_deprecated ? (
                  <span className='text-[var(--color-text-secondary)]'>Deprecated</span>
                ) : (
                  <span className='text-green-600 dark:text-green-400'>Active</span>
                )}
              </td>
              <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                <button
                  onClick={() => onEdit(tier)}
                  className='text-[var(--color-accent)] hover:brightness-110 mr-4'
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(tier)}
                  className='text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300'
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </DualScrollTable>
      <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />

      {tiers.length === 0 && (
        <div className='text-center py-8 text-[var(--color-text-secondary)]'>No SLA tiers found</div>
      )}

      <Modal
        isOpen={deleteConfirm && !!deletingTier}
        onClose={() => {
          setDeleteConfirm(false);
          setDeletingTier(null);
        }}
        title='Confirm Deletion'
        size='sm'
        footer={
          affectedClients.length > 0 ? (
            <>
              <Button
                variant='outline'
                onClick={() => {
                  setDeleteConfirm(false);
                  setDeletingTier(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => confirmDelete(true)}
                className='bg-[var(--color-status-warning)] text-white hover:brightness-110 focus:ring-[var(--color-status-warning)]'
              >
                Deprecate Instead
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='outline'
                onClick={() => {
                  setDeleteConfirm(false);
                  setDeletingTier(null);
                }}
              >
                Cancel
              </Button>
              <Button variant='danger' onClick={() => confirmDelete(false)}>
                Delete
              </Button>
            </>
          )
        }
      >
        {affectedClients.length > 0 ? (
          <div>
            <p className='text-[var(--color-text-secondary)] mb-4'>
              This tier is assigned to {affectedClients.length} client(s):
            </p>
            <ul className='list-disc list-inside mb-4 max-h-40 overflow-y-auto'>
              {affectedClients.map((client) => (
                <li key={client.client_id} className='text-sm text-[var(--color-text-secondary)]'>
                  {client.client_name} ({client.assignment_count} assignment
                  {client.assignment_count > 1 ? 's' : ''})
                </li>
              ))}
            </ul>
            <p className='text-[var(--color-text-secondary)] mb-4'>
              You cannot delete this tier. Would you like to deprecate it instead?
            </p>
          </div>
        ) : (
          <p className='text-[var(--color-text-secondary)]'>
            Are you sure you want to delete "{deletingTier?.tier_name}"? This action cannot be
            undone.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default TierList;
